import { GuestHotelScheduleLiteModel } from 'api-hooks/cro/model';
import { StatefulTooltip } from 'baseui/tooltip';
import { hotelCheckInOptions } from 'components/elements/table/editable-columns/check-in-options';
import {
  ColumnDataType,
  IColumn as EditableIColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import useDate from 'hooks/use-date';
import useTranslation from 'next-translate/useTranslation';

import CheckInSelect from '../components/check-in-select';
import HotelConfirmationCodeInput from '../components/hotel-confirmation-code-input';
import HotelScheduleCheckboxHeader from '../components/hotel-schedule/hotel-schedule-checkbox-header';

export default function useHotelScheduleTableList(
  type: 'default' | 'editable' = 'default',
) {
  const { t } = useTranslation();
  const formatDate = useDate();
  const isEditable = type === 'editable';

  const checkBoxColumn: EditableIColumn<GuestHotelScheduleLiteModel>[] =
    isEditable
      ? [
          {
            dataType: ColumnDataType.checkbox,
            accessorKey: 'isCheck',
            header: () => <HotelScheduleCheckboxHeader />,
            maxSize: 42,
            minSize: 42,
            stickyLeft: true,
          },
        ]
      : [];

  return [
    ...checkBoxColumn,
    {
      dataType: ColumnDataType.select,
      accessorKey: 'checkInStatus',
      header: t('common:status'),
      minSize: '200px',
      maxSize: '200px',
      cell: isEditable
        ? undefined
        : ({ row }) => {
            return (
              <CheckInSelect
                type="hotel"
                key={row.original.id}
                original={row.original}
              />
            );
          },
      options: hotelCheckInOptions(t),
    },
    {
      dataType: ColumnDataType.text,
      minSize: '200px',
      maxSize: '200px',
      accessorKey: 'hotelConfirmationCode',
      header: t('common:hotel_confirmation'),
      cell: isEditable
        ? undefined
        : ({ row }) => {
            return <HotelConfirmationCodeInput original={row.original} />;
          },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'checkIn',
      header: t('common:check_in'),
      sortName: 'check_in',
      maxSize: 120,
      minSize: 120,
      cell: ({ row }) => {
        const checkin = row.original.checkIn;
        return checkin ? formatDate(checkin, 'dd-MM-yyyy') : '-';
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'checkOut',
      header: t('common:check_out'),
      sortName: 'check_out',
      maxSize: 120,
      minSize: 120,
      cell: ({ row }) => {
        const checkout = row.original.checkOut;
        return checkout ? formatDate(checkout, 'dd-MM-yyyy') : '-';
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'hotel',
      header: t('common:hotel'),
      sortName: 'hotel',
      maxSize: 120,
      minSize: 120,
      cell({ row }) {
        return row.original.hotel.name;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'customer',
      header: t('common:customer'),
      sortName: 'customer_name',
      minSize: 180,
      cell({ row }) {
        return row.original.customer.name;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'guests',
      header: t('common:guest'),
      minSize: 180,
      cell({ row }) {
        const preview = row.original.guests.slice(0, 5);
        const more = row.original.guests.slice(5);

        const guestComponennts = (isMore: boolean = true) => (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {(isMore ? more : preview).map((guest) => {
              return (
                <Text variant="ParagraphSmall" key={guest.id}>
                  {guest.name}
                </Text>
              );
            })}
          </div>
        );

        return (
          <>
            {guestComponennts(false)}
            {more.length !== 0 && (
              <StatefulTooltip
                content={() => guestComponennts()}
                overrides={{
                  Body: {
                    style: {
                      zIndex: 10,
                    },
                  },
                  Inner: {
                    style: {
                      backgroundColor: 'white',
                    },
                  },
                }}
              >
                {t('common:more')}
              </StatefulTooltip>
            )}
          </>
        );
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'operator',
      header: t('common:operator'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const operator = row.original.operator?.name;
        return operator;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'sales',
      header: t('common:sales'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const sales = row.original.sales?.name;
        return sales;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'lead',
      header: t('invoice:lead'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const lead = row.original.lead?.name;
        return lead;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'invoiceNumber',
      header: t('common:invoice_number'),
      maxSize: 180,
      minSize: 180,
      cell({ row }) {
        return row.original.invoiceNumber;
      },
    },
  ] as any;
}
